import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Sparkles, ArrowRight, Mail, Lock, User,
  Users, Brain, Swords, Share2, Users2,
  Zap, GitBranch, ShieldCheck, DollarSign, Repeat, Eye,
  Target, Check, X, TrendingUp, FileText, MessageSquare,
  ChevronDown, Rocket
} from "lucide-react";
import Logo from "../components/Logo";
import { BrainMarquee } from "../components/BrainConstellation";

const FEATURE_TILES = [
  { icon: Users,   t: "Persistent workers", d: "Name, mission, memory. Yours forever.", c: "#3B82F6" },
  { icon: Brain,   t: "12 swappable brains", d: "OpenAI · Claude · Gemini · DeepSeek …", c: "#10B981" },
  { icon: Swords,  t: "Debate mode",        d: "Watch models fight. Judge decides.",    c: "#EF4444" },
  { icon: Users2,  t: "Teams & templates",  d: "Group experts. Clone the best setups.", c: "#8B5CF6" },
  { icon: Share2,  t: "Public share links", d: "Any worker or debate → one URL.",        c: "#D97706" },
];

export default function Landing() {
  const { login, register, loginWithGoogle, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    nav("/dashboard");
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name || email.split("@")[0]);
      }
      nav("/dashboard");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5] flex flex-col">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between max-w-7xl mx-auto w-full">
        <Link to="/about" data-testid="brand">
          <Logo size={40} wordmark="full" tagline />
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/models" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="models-link">
            <Brain size={14} strokeWidth={2.5} /> Models
          </Link>
          <Link to="/showcase" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="showcase-link">
            <Sparkles size={14} strokeWidth={2.5} /> Showcase
          </Link>
          <Link to="/creators" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="creators-link">
            <Users size={14} strokeWidth={2.5} /> Creators
          </Link>
          <Link to="/about" className="text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="about-link">
            About
          </Link>
        </div>
      </header>

      {/* Hero / Auth */}
      <div className="flex-1 grid md:grid-cols-2 gap-8 px-6 md:px-12 lg:px-20 py-8 items-center max-w-7xl mx-auto w-full">
        {/* Left */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest" data-testid="hero-pill">
            <span className="w-2 h-2 rounded-full bg-emerald-500 dot-blink" />
            12 brains · 8 providers · 1 workforce
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Stop switching tabs.<br />
            <span className="bg-amber-200 px-2 -rotate-1 inline-block border-2 border-slate-900 rounded-lg">Orchestrate</span> the whole AI stack.
          </h1>

          <p className="text-lg text-slate-600 max-w-md font-medium leading-relaxed">
            One workspace. Twelve state-of-the-art brains — <strong>OpenAI, Claude, Gemini, DeepSeek, Qwen, Kimi, Llama, Mistral</strong>. Build persistent AI workers, put them in live debates, or wire them into visual workflows. Never rewrite a prompt when a better model drops.
          </p>

          <div className="grid grid-cols-2 gap-2.5 max-w-md pt-2" data-testid="feature-tiles">
            {FEATURE_TILES.map(({ icon: I, t, d, c }, i) => (
              <div key={i} className="brut-card p-3 flex items-start gap-2.5" data-testid={`tile-${i}`}>
                <div
                  className="w-9 h-9 shrink-0 rounded-lg border-2 border-slate-900 flex items-center justify-center"
                  style={{ backgroundColor: c + "22", color: c }}
                >
                  <I size={16} strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm leading-tight">{t}</div>
                  <div className="text-[11px] text-slate-500 leading-snug">{d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs pt-2">
            <Link
              to="/models"
              className="inline-flex items-center gap-1.5 font-heading font-bold text-slate-900 border-b-2 border-slate-900 hover:border-amber-400 transition-colors"
              data-testid="see-models"
            >
              <Brain size={12} strokeWidth={3} /> Compare all 12 brains →
            </Link>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500 font-mono">$0.01 per debate · &lt;800ms first byte</span>
          </div>
        </div>

        {/* Right: Auth card */}
        <div className="brut-card p-7 md:p-9 max-w-md w-full mx-auto" data-testid="auth-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold">
              {mode === "login" ? "Welcome back" : "Hire your first team"}
            </h2>
            <div className="bg-slate-100 border-2 border-slate-900 rounded-full p-1 flex text-xs font-heading font-bold">
              <button
                data-testid="tab-login"
                onClick={() => setMode("login")}
                className={`px-3 py-1 rounded-full ${mode === "login" ? "bg-slate-900 text-white" : "text-slate-700"}`}
              >Sign in</button>
              <button
                data-testid="tab-register"
                onClick={() => setMode("register")}
                className={`px-3 py-1 rounded-full ${mode === "register" ? "bg-slate-900 text-white" : "text-slate-700"}`}
              >Sign up</button>
            </div>
          </div>

          {mode === "register" && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border-2 border-slate-900 text-xs text-slate-700">
              <b className="text-slate-900">You'll start with 4 workers + 1 team</b> auto-seeded so you can run a debate in 30 seconds.
            </div>
          )}

          <button
            data-testid="google-login-btn"
            onClick={loginWithGoogle}
            className="w-full brut-btn brut-btn-outline mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.2C40 35.3 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-slate-300" />
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">or</span>
            <div className="flex-1 h-px bg-slate-300" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    data-testid="register-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="brut-input pl-9"
                    placeholder="Ada Lovelace"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  data-testid="email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brut-input pl-9"
                  placeholder="you@studio.dev"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  data-testid="password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="brut-input pl-9"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {err && (
              <div className="text-sm font-semibold text-red-600 bg-red-100 border-2 border-red-500 rounded-lg p-2" data-testid="auth-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full brut-btn"
            >
              {busy ? "..." : mode === "login" ? "Sign in" : "Create my workforce"}
              <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </form>
        </div>
      </div>

      {/* Brain marquee — the money strip */}
      <div className="mt-4" data-testid="brain-strip">
        <BrainMarquee speed={45} />
      </div>

      {/* ==================== WHY AIMEDLEY ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-7xl mx-auto w-full" data-testid="why-section">
        <div className="max-w-3xl mb-12">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">Why AiMedley</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
            One subscription. <span className="bg-amber-200 px-2 border-2 border-slate-900 rounded-lg">Every</span> frontier model. Zero vendor lock-in.
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            You already know that GPT is better at reasoning, Claude at writing, Gemini at long context, DeepSeek at math, and Llama at cost.
            Today you juggle five tabs, five prompts, five bills. AiMedley collapses that into one workspace where models compete, cooperate, and hand off — with a single audit trail and one budget.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="why-pillars">
          <PillarCard
            icon={Brain}
            color="#3B82F6"
            title="Best-of-breed, on demand"
            body="Route each task to the model that's best at it. Swap brains without touching your prompts. When a new model drops on Tuesday, use it Tuesday — no migration, no rebuild."
            proof="12 brains · 8 providers"
            testid="pillar-brains"
          />
          <PillarCard
            icon={Users}
            color="#059669"
            title="Personas that persist"
            body="Your AI workers keep their name, mission, memory, and voice — even when you upgrade the brain behind them. Build once, keep forever."
            proof="Persistent memory · Team leaders · Shared context"
            testid="pillar-personas"
          />
          <PillarCard
            icon={GitBranch}
            color="#B45309"
            title="Real orchestration, not chat"
            body="Chain workers through visual workflows. Add reviewers, human approval, live web search, calculators, or debates. Every run is timestamped, priced, and exportable to PDF."
            proof="9 node types · Parallel execution · Human-in-the-loop"
            testid="pillar-orchestration"
          />
        </div>
      </section>

      {/* ==================== COMPARISON: WITHOUT vs WITH ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-7xl mx-auto w-full" data-testid="compare-section">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">The upgrade</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            The daily AI workflow, before &amp; after
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="brut-card p-6 md:p-8 bg-red-50" style={{ borderColor: "#B91C1C", boxShadow: "6px 6px 0 #B91C1C" }} data-testid="before-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-white flex items-center justify-center">
                <X size={18} strokeWidth={3} className="text-red-600" />
              </div>
              <div className="text-xs uppercase tracking-widest font-heading font-bold text-red-700">Today, without AiMedley</div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <BadPoint>Five browser tabs, five UIs, five typed-in prompts — every session</BadPoint>
              <BadPoint>You forget which model wrote which draft. Nothing has memory of yesterday</BadPoint>
              <BadPoint>Comparing answers means copy-pasting into a Google Doc side-by-side</BadPoint>
              <BadPoint>You can't audit cost per task, per team, or per project</BadPoint>
              <BadPoint>When a better model launches, you rewrite every prompt. Again.</BadPoint>
              <BadPoint>No way to loop a human into the middle of a long chain</BadPoint>
            </ul>
          </div>

          <div className="brut-card p-6 md:p-8 bg-emerald-50" style={{ borderColor: "#047857", boxShadow: "6px 6px 0 #047857" }} data-testid="after-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-white flex items-center justify-center">
                <Check size={18} strokeWidth={3} className="text-emerald-600" />
              </div>
              <div className="text-xs uppercase tracking-widest font-heading font-bold text-emerald-700">With AiMedley</div>
            </div>
            <ul className="space-y-3 text-sm text-slate-800 leading-relaxed">
              <GoodPoint>One workspace, one login, one bill for every model in the market</GoodPoint>
              <GoodPoint>Workers remember every conversation, every decision, every document</GoodPoint>
              <GoodPoint>Compare mode fans out one prompt to 4 brains — side-by-side, streaming, priced</GoodPoint>
              <GoodPoint>Every run tracks cost, tokens, and brain — filterable by worker, team, project</GoodPoint>
              <GoodPoint>Hot-swap the brain. Persona and history persist. Zero prompt rewrites.</GoodPoint>
              <GoodPoint>Human Approval nodes pause a run until you click. Then it resumes exactly where it left off.</GoodPoint>
            </ul>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-16 bg-white border-y-2 border-slate-900" data-testid="how-section">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">How it works</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Ship your first AI worker in under 3 minutes
            </h2>
            <p className="text-slate-600">Sign up gives you 4 pre-configured workers + 1 team, so you can run something real on day one.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-5" data-testid="how-steps">
            <Step
              n="01" icon={User} color="#3B82F6"
              title="Hire a worker"
              body="Give them a name, mission, and instructions. Pick a brain — or let Auto Brain choose the cheapest capable one for the job."
            />
            <Step
              n="02" icon={MessageSquare} color="#059669"
              title="Talk, compare, research"
              body="Chat 1:1 with any worker. Compare 4 answers in parallel. Synthesize research across every brain — with citations and cost."
            />
            <Step
              n="03" icon={Swords} color="#DC2626"
              title="Debate or orchestrate"
              body="Put 2–5 workers in a live debate with a judge. Or drop them into a visual workflow with reviewers, approvals, and tools."
            />
            <Step
              n="04" icon={Share2} color="#B45309"
              title="Share, export, iterate"
              body="Every worker, debate, and run has a public share link, a Markdown export, and a polished PDF — one click each."
            />
          </div>
        </div>
      </section>

      {/* ==================== FEATURE DEEP-DIVE ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-7xl mx-auto w-full space-y-16" data-testid="deepdive-section">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">What's inside</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Three layers. One workspace.
          </h2>
        </div>

        <DeepDiveRow
          testid="deep-workbench"
          badge="LAYER 1 · WORKBENCH"
          title="Every model, one prompt away"
          body="Chat, Compare, Research, and Document Q&A across 12 brains — with real-time streaming and live cost per token. Perfect for solo work when you just need the best answer, fast."
          bullets={[
            "Compare fans out one prompt to up to 4 brains side-by-side",
            "Research mode synthesizes multi-model answers into a single report with citations",
            "Upload PDFs and docs — ground every answer on your own library",
          ]}
          highlight={{
            icon: TrendingUp,
            label: "First byte",
            value: "<800ms",
            sub: "streaming SSE",
          }}
          accent="#3B82F6"
        />

        <DeepDiveRow
          testid="deep-workforce"
          reverse
          badge="LAYER 2 · WORKFORCE"
          title="Personas that outlive vendors"
          body="Persistent AI workers with memory, skills, and a distinct voice. Group them into teams with a leader and shared context. Set daily and monthly budgets per worker so nothing runs away."
          bullets={[
            "Swap a worker's brain without losing their persona, memory, or history",
            "Teams share instructions and memory — like a real cross-functional squad",
            "Budgets, analytics, and brain-swap history for every worker",
          ]}
          highlight={{
            icon: Repeat,
            label: "Brain swap",
            value: "1 click",
            sub: "history preserved",
          }}
          accent="#059669"
        />

        <DeepDiveRow
          testid="deep-orchestration"
          badge="LAYER 3 · ORCHESTRATION"
          title="Visual workflows that actually ship"
          body="Drop nodes onto a canvas: workers, reviewers, human approvals, conditions, debates, tools, and knowledge search. Independent branches run in parallel. Every run is a full audit trail — priced, timestamped, exportable to PDF."
          bullets={[
            "9 node types including Human Approval that pauses the run until you click",
            "Parallel branch execution — independent nodes gathered by asyncio, not sequential",
            "Every step tracks brain, tokens, cost, and output — filterable and shareable",
          ]}
          highlight={{
            icon: GitBranch,
            label: "Node types",
            value: "9",
            sub: "including debate + tools",
          }}
          accent="#B45309"
        />
      </section>

      {/* ==================== BUILT FOR ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-16 bg-[#FBF5EE] border-y-2 border-slate-900" data-testid="builtfor-section">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">Built for</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Teams that treat AI as infrastructure, not a toy
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="persona-cards">
            <Persona
              icon={Rocket} color="#3B82F6"
              who="Solo builders"
              what="Ship AI-heavy features without picking a lifelong vendor. Prototype with Llama, ship with Claude, scale on GPT."
            />
            <Persona
              icon={FileText} color="#059669"
              who="Analysts &amp; researchers"
              what="Fan out one question to 4 brains, then synthesize. Ground on your PDFs. Export the audit trail to PDF."
            />
            <Persona
              icon={Users2} color="#8B5CF6"
              who="Product &amp; strategy"
              what="Put competing arguments in the ring. Judge weighs in. Audience polls on public share links close the loop."
            />
            <Persona
              icon={ShieldCheck} color="#B45309"
              who="RevOps &amp; enterprise"
              what="Structured workflows with approvals, budgets, and per-team cost. JWT auth, encrypted keys, PDF audit exports."
            />
          </div>
        </div>
      </section>

      {/* ==================== STATS STRIP ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-12 max-w-7xl mx-auto w-full" data-testid="stats-strip">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat testid="stat-brains"    v="12"       l="frontier brains"  s="Auto Brain routes" />
          <Stat testid="stat-first"     v="<800ms"   l="first byte"        s="SSE streaming" />
          <Stat testid="stat-cost"      v="~$0.01"   l="per live debate"   s="4 debaters + judge" />
          <Stat testid="stat-nodes"     v="9"        l="workflow node types" s="incl. tools + debate" />
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-4xl mx-auto w-full" data-testid="faq-section">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-amber-700 mb-3">Common questions</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Everything you'll ask in the first 5 minutes
          </h2>
        </div>
        <div className="space-y-3" data-testid="faq-list">
          <Faq
            q="Do I need my own OpenAI / Anthropic / Google keys?"
            a="No. AiMedley ships with a universal LLM key that works out of the box for every one of the 12 brains. If you'd rather use your own keys for compliance or billing, drop them in Providers — they're encrypted at rest and used per-request."
          />
          <Faq
            q="How is this different from just using ChatGPT or Claude?"
            a="ChatGPT and Claude each give you one brain. AiMedley gives you twelve, plus persistent workers with memory, plus Compare mode, plus Debate mode, plus a visual workflow builder, plus cost tracking. It's the difference between a hammer and a workbench."
          />
          <Faq
            q="What happens to my worker when GPT-6 or Claude 5 launches?"
            a="Nothing breaks. You open the worker, swap their brain in a dropdown, and they keep every conversation, every memory, and every skill. That's the whole point — personas that outlive vendors."
          />
          <Faq
            q="Can I export what my workers produce?"
            a="Yes. Every debate, workflow run, and shared conversation exports to Markdown and to A4-styled PDF — one click each. Public share links work without a login for stakeholders."
          />
          <Faq
            q="Is my data used to train anyone's model?"
            a="No. Requests are routed through provider APIs with training opt-out flags set. Your documents live in your database only; nothing is sent to third parties beyond the model call itself."
          />
          <Faq
            q="How much does it cost to run?"
            a="Same as the underlying model price plus a small routing surcharge — visible on every run. A typical 4-debater debate with a judge runs around $0.01. Chat and Compare are pay-as-you-go per token."
          />
        </div>
      </section>

      {/* ==================== BOTTOM CTA ==================== */}
      <section className="px-6 md:px-12 lg:px-20 py-16" data-testid="bottom-cta">
        <div
          className="max-w-4xl mx-auto brut-card p-8 md:p-12 text-center text-white"
          style={{ borderColor: "#0F172A", boxShadow: "8px 8px 0 #B45309", background: "#0F172A" }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest text-slate-900 mb-5">
            <Sparkles size={12} strokeWidth={3} />
            Start free · 4 workers pre-seeded
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-4">
            Your AI workforce is <span className="bg-amber-300 text-slate-900 px-2 -rotate-1 inline-block border-2 border-amber-100 rounded-lg">waiting</span> to be hired.
          </h2>
          <p className="text-slate-300 text-lg mb-7 max-w-xl mx-auto leading-relaxed">
            30 seconds to sign up. 3 minutes to your first debate. Zero prompt rewrites when the next model drops.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="brut-btn bg-amber-300 text-slate-900 border-amber-100 hover:bg-amber-200"
              style={{ boxShadow: "4px 4px 0 #FFFFFF" }}
              data-testid="cta-signup"
            >
              <Rocket size={16} strokeWidth={2.5} /> Create my workforce
            </a>
          </div>
        </div>
      </section>

      <footer className="px-6 py-6 text-center text-xs text-slate-400 font-semibold">
        AiMedley · Where personas outlive vendors · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

/* ==================== Reusable content blocks ==================== */

function PillarCard({ icon: I, color, title, body, proof, testid }) {
  return (
    <div className="brut-card p-6" style={{ borderColor: color, boxShadow: `5px 5px 0 ${color}` }} data-testid={testid}>
      <div className="w-11 h-11 rounded-lg border-2 border-slate-900 flex items-center justify-center mb-4" style={{ background: color + "22", color }}>
        <I size={20} strokeWidth={2.5} />
      </div>
      <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">{body}</p>
      <div className="text-[10px] uppercase tracking-widest font-heading font-bold" style={{ color }}>
        {proof}
      </div>
    </div>
  );
}

function BadPoint({ children }) {
  return (
    <li className="flex items-start gap-2">
      <X size={14} strokeWidth={3} className="text-red-600 mt-1 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function GoodPoint({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check size={14} strokeWidth={3} className="text-emerald-600 mt-1 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function Step({ n, icon: I, color, title, body }) {
  return (
    <div className="brut-card p-5" style={{ borderColor: color, boxShadow: `4px 4px 0 ${color}` }} data-testid={`step-${n}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-heading text-2xl font-bold text-slate-300">{n}</span>
        <div className="w-9 h-9 rounded-lg border-2 border-slate-900 ml-auto flex items-center justify-center" style={{ background: color + "22", color }}>
          <I size={16} strokeWidth={2.5} />
        </div>
      </div>
      <h3 className="font-heading text-lg font-bold mb-1.5">{title}</h3>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function DeepDiveRow({ badge, title, body, bullets, highlight, accent, reverse, testid }) {
  const HIcon = highlight.icon;
  return (
    <div className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-heading font-bold mb-3" style={{ color: accent }}>
          {badge}
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-3">{title}</h3>
        <p className="text-slate-600 leading-relaxed mb-4">{body}</p>
        <ul className="space-y-2">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
              <Check size={14} strokeWidth={3} className="mt-1 shrink-0" style={{ color: accent }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="brut-card p-8 flex flex-col items-center justify-center min-h-[220px]" style={{ borderColor: accent, boxShadow: `6px 6px 0 ${accent}`, background: accent + "11" }}>
        <div className="w-16 h-16 rounded-xl border-2 border-slate-900 bg-white flex items-center justify-center mb-4" style={{ color: accent }}>
          <HIcon size={28} strokeWidth={2.5} />
        </div>
        <div className="font-heading text-5xl font-bold tracking-tight" style={{ color: accent }}>
          {highlight.value}
        </div>
        <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mt-2">
          {highlight.label}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">{highlight.sub}</div>
      </div>
    </div>
  );
}

function Persona({ icon: I, color, who, what }) {
  return (
    <div className="brut-card p-5" style={{ borderColor: color, boxShadow: `4px 4px 0 ${color}` }} data-testid={`persona-${who.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="w-10 h-10 rounded-lg border-2 border-slate-900 flex items-center justify-center mb-3" style={{ background: color + "22", color }}>
        <I size={16} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-bold text-base mb-1" dangerouslySetInnerHTML={{ __html: who }} />
      <p className="text-xs text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: what }} />
    </div>
  );
}

function Stat({ v, l, s, testid }) {
  return (
    <div className="brut-card p-5 text-center" data-testid={testid}>
      <div className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900">{v}</div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-600 mt-1">{l}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">{s}</div>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  const slug = q.slice(0, 24).toLowerCase().replace(/[^a-z]/g, "-");
  return (
    <details
      className="brut-card p-0 overflow-hidden group"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid={`faq-${slug}`}
    >
      <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 list-none font-heading font-bold text-slate-900 hover:bg-amber-50">
        <ChevronDown size={16} strokeWidth={3} className={`shrink-0 transition-transform ${open ? "rotate-180 text-amber-600" : "text-slate-400"}`} />
        <span className="flex-1">{q}</span>
      </summary>
      <div className="px-5 pb-5 pt-1 text-sm text-slate-600 leading-relaxed border-t-2 border-slate-100">
        {a}
      </div>
    </details>
  );
}
